/**
 * Organization Finance Context
 * Manages org finance state with subscription awareness
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import orgFinanceService from '../services/orgFinanceService';
import { toast } from 'sonner';

const OrgFinanceContext = createContext(null);

export const useOrgFinance = () => {
  const context = useContext(OrgFinanceContext);
  if (!context) {
    throw new Error('useOrgFinance must be used within an OrgFinanceProvider');
  }
  return context;
};

export const OrgFinanceProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentOrganization: organization } = useOrganization();
  
  // Subscription state
  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [isTrialActive, setIsTrialActive] = useState(false);
  const [trialDaysRemaining, setTrialDaysRemaining] = useState(0);
  const [canActivate, setCanActivate] = useState(false);
  
  // Overview state
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  
  // Analytics state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  
  // Budget requests state
  const [budgetRequests, setBudgetRequests] = useState([]);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  
  // Department budgets state
  const [departmentBudgets, setDepartmentBudgets] = useState([]);
  
  // Expenses state
  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(false);
  
  // Trial info banner state
  const [trialInfo, setTrialInfo] = useState(null);
  
  // Finance Department Configuration state
  const [financeConfig, setFinanceConfig] = useState(null);
  const [isFinanceUser, setIsFinanceUser] = useState(false);
  const [financeConfigLoading, setFinanceConfigLoading] = useState(false);
  
  // Dashboard state (role-based view)
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardViewType, setDashboardViewType] = useState('department_portal'); // 'finance_admin' or 'department_portal'
  
  // Error state
  const [error, setError] = useState(null);

  // ============================================
  // SUBSCRIPTION MANAGEMENT
  // ============================================
  
  const fetchSubscription = useCallback(async () => {
    if (!organization?._id) {
      setSubscriptionLoading(false);
      return;
    }
    
    try {
      setSubscriptionLoading(true);
      setError(null);
      const response = await orgFinanceService.getSubscription();
      
      if (response.success) {
        setSubscription(response.data);
        setCanActivate(response.canActivate || false);
        
        if (response.data) {
          const isTrial = response.data.status === 'trial';
          setIsTrialActive(isTrial);
          
          if (isTrial && response.data.trialEndDate) {
            const endDate = new Date(response.data.trialEndDate);
            const now = new Date();
            const diffTime = endDate - now;
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            setTrialDaysRemaining(Math.max(0, diffDays));
          }
        } else {
          setIsTrialActive(false);
          setTrialDaysRemaining(0);
        }
      }
    } catch (err) {
      console.error('Error fetching subscription:', err);
      setError(err.message);
    } finally {
      setSubscriptionLoading(false);
    }
  }, [organization?._id]);

  const activateTrial = useCallback(async () => {
    try {
      setSubscriptionLoading(true);
      const response = await orgFinanceService.startTrial();
      
      if (response.success) {
        toast.success('Finance app trial activated! You have 14 days of full access.');
        await fetchSubscription();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error activating trial:', err);
      toast.error(err.response?.data?.message || 'Failed to activate trial');
      return false;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [fetchSubscription]);

  const upgradeSubscription = useCallback(async (upgradeData) => {
    try {
      setSubscriptionLoading(true);
      const response = await orgFinanceService.upgradeSubscription(upgradeData);
      
      if (response.success) {
        toast.success(`Successfully upgraded to ${upgradeData.tier} plan!`);
        await fetchSubscription();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error upgrading subscription:', err);
      toast.error(err.response?.data?.message || 'Failed to upgrade subscription');
      return false;
    } finally {
      setSubscriptionLoading(false);
    }
  }, [fetchSubscription]);

  // ============================================
  // FINANCE DEPARTMENT CONFIGURATION
  // ============================================

  const fetchFinanceConfig = useCallback(async () => {
    if (!subscription) return;
    
    try {
      setFinanceConfigLoading(true);
      const response = await orgFinanceService.getFinanceConfig();
      
      if (response.success) {
        setFinanceConfig(response.data);
        setIsFinanceUser(response.isFinanceUser || false);
      }
    } catch (err) {
      // 403 errors are expected for non-finance users - handle silently
      if (err?.response?.status !== 403) {
        console.warn('Error fetching finance config:', err?.message || err);
      }
    } finally {
      setFinanceConfigLoading(false);
    }
  }, [subscription]);

  const configureFinanceDepartment = useCallback(async (configData) => {
    try {
      setFinanceConfigLoading(true);
      const response = await orgFinanceService.configureFinanceDepartment(configData);
      
      if (response.success) {
        toast.success('Finance department configured successfully');
        setFinanceConfig(response.data);
        return true;
      }
      return false;
    } catch (err) {
      // 403 errors are expected for non-admin users
      if (err?.response?.status !== 403) {
        console.warn('Error configuring finance department:', err?.message || err);
      }
      toast.error(err.response?.data?.message || 'Failed to configure finance department');
      return false;
    } finally {
      setFinanceConfigLoading(false);
    }
  }, []);

  // ============================================
  // DASHBOARD (Role-Based Views)
  // ============================================

  const fetchDashboard = useCallback(async () => {
    if (!subscription) return;
    
    try {
      setDashboardLoading(true);
      const response = await orgFinanceService.getDashboard();
      
      if (response.success) {
        setDashboardData(response.data);
        setDashboardViewType(response.viewType || 'department_portal');
        setIsFinanceUser(response.isFinanceUser || false);
        
        // For non-finance users, set departmentBudgets from dashboard response
        // since getAllDepartmentBudgets API is restricted to finance users only
        if (!response.isFinanceUser && response.data?.departmentBudgets) {
          setDepartmentBudgets(response.data.departmentBudgets);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setDashboardLoading(false);
    }
  }, [subscription]);

  // ============================================
  // OVERVIEW MANAGEMENT
  // ============================================

  const fetchOverview = useCallback(async () => {
    if (!subscription) return;
    
    try {
      setOverviewLoading(true);
      const response = await orgFinanceService.getOverview();
      
      if (response.success) {
        setOverview(response.data);
        setTrialInfo(response.trialInfo);
      }
    } catch (err) {
      console.error('Error fetching overview:', err);
    } finally {
      setOverviewLoading(false);
    }
  }, [subscription]);

  const updateOverview = useCallback(async (overviewData) => {
    try {
      const response = await orgFinanceService.updateOverview(overviewData);
      
      if (response.success) {
        setOverview(response.data);
        toast.success('Finance overview updated');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error updating overview:', err);
      toast.error(err.response?.data?.message || 'Failed to update overview');
      return false;
    }
  }, []);

  // ============================================
  // ANALYTICS
  // ============================================

  const fetchAnalytics = useCallback(async (params = {}) => {
    if (!subscription) return;
    
    try {
      setAnalyticsLoading(true);
      const response = await orgFinanceService.getAnalytics(params);
      
      if (response.success) {
        setAnalytics(response.data);
        setPendingRequestsCount(response.data?.pendingRequests || 0);
      }
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [subscription]);

  // ============================================
  // DEPARTMENT BUDGETS
  // ============================================

  const fetchDepartmentBudgets = useCallback(async (fiscalYear = new Date().getFullYear().toString()) => {
    if (!subscription) return;
    
    try {
      const response = await orgFinanceService.getAllDepartmentBudgets({ fiscalYear });
      
      if (response.success) {
        setDepartmentBudgets(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching department budgets:', err);
    }
  }, [subscription]);

  // ============================================
  // EXPENSES
  // ============================================

  const fetchExpenses = useCallback(async (params = {}) => {
    if (!subscription) return;
    
    try {
      setExpensesLoading(true);
      const response = await orgFinanceService.getExpenses(params);
      
      if (response.success) {
        setExpenses(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching expenses:', err);
    } finally {
      setExpensesLoading(false);
    }
  }, [subscription]);

  // ============================================
  // BUDGET REQUESTS
  // ============================================

  const fetchBudgetRequests = useCallback(async (params = {}) => {
    if (!subscription) return;
    
    try {
      const response = await orgFinanceService.getBudgetRequests(params);
      
      if (response.success) {
        setBudgetRequests(response.data || []);
        // Count pending/submitted requests
        const pending = (response.data || []).filter(
          r => r.status === 'pending' || r.status === 'submitted'
        ).length;
        setPendingRequestsCount(pending);
      }
    } catch (err) {
      console.error('Error fetching budget requests:', err);
    }
  }, [subscription]);

  const createBudgetRequest = useCallback(async (requestData) => {
    try {
      const response = await orgFinanceService.createBudgetRequest(requestData);
      
      if (response.success) {
        toast.success('Budget request submitted to Finance Department');
        await fetchBudgetRequests();
        return response.data;
      }
      return null;
    } catch (err) {
      console.error('Error creating budget request:', err);
      toast.error(err.response?.data?.message || 'Failed to submit budget request');
      return null;
    }
  }, [fetchBudgetRequests]);

  const approveBudgetRequest = useCallback(async (requestId, approvalData = {}) => {
    try {
      const response = await orgFinanceService.approveBudgetRequest(requestId, approvalData);
      
      if (response.success) {
        toast.success('Budget request approved');
        await fetchBudgetRequests();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error approving budget request:', err);
      toast.error(err.response?.data?.message || 'Failed to approve request');
      return false;
    }
  }, [fetchBudgetRequests]);

  const rejectBudgetRequest = useCallback(async (requestId, reason) => {
    try {
      const response = await orgFinanceService.rejectBudgetRequest(requestId, reason);
      
      if (response.success) {
        toast.success('Budget request rejected');
        await fetchBudgetRequests();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error rejecting budget request:', err);
      toast.error(err.response?.data?.message || 'Failed to reject request');
      return false;
    }
  }, [fetchBudgetRequests]);

  const requestClarification = useCallback(async (requestId, message) => {
    try {
      const response = await orgFinanceService.requestClarification(requestId, message);
      
      if (response.success) {
        toast.success('Clarification requested');
        await fetchBudgetRequests();
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error requesting clarification:', err);
      toast.error(err.response?.data?.message || 'Failed to request clarification');
      return false;
    }
  }, [fetchBudgetRequests]);

  const addRequestComment = useCallback(async (requestId, comment, isInternal = false) => {
    try {
      const response = await orgFinanceService.addRequestComment(requestId, comment, isInternal);
      
      if (response.success) {
        toast.success('Comment added');
        return true;
      }
      return false;
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error(err.response?.data?.message || 'Failed to add comment');
      return false;
    }
  }, []);

  // ============================================
  // SUBSCRIPTION CHECK HELPERS (defined early for use in hooks)
  // ============================================

  const hasActiveSubscription = subscription?.status === 'active' || subscription?.status === 'trial';
  const isSubscriptionExpired = subscription?.status === 'expired' || subscription?.status === 'cancelled';

  // ============================================
  // REFRESH ALL DATA
  // ============================================

  const refreshData = useCallback(async () => {
    if (!hasActiveSubscription) return;
    
    try {
      await Promise.all([
        fetchFinanceConfig(),
        fetchDashboard(),
        fetchOverview(),
        fetchAnalytics(),
        fetchDepartmentBudgets(),
        fetchBudgetRequests(),
        fetchExpenses()
      ]);
    } catch (err) {
      // 403 errors are expected for non-finance users - handle silently
      if (err?.response?.status !== 403) {
        console.warn('Error refreshing finance data:', err?.message || err);
      }
    }
  }, [hasActiveSubscription, fetchFinanceConfig, fetchDashboard, fetchOverview, fetchAnalytics, fetchDepartmentBudgets, fetchBudgetRequests, fetchExpenses]);

  // ============================================
  // CHECK IF SETUP IS NEEDED
  // ============================================

  const needsSetup = hasActiveSubscription && !financeConfigLoading && !financeConfig?.financeDepartmentId;

  // ============================================
  // PERMISSION HELPERS
  // ============================================

  const hasFinancePermission = useCallback((permission) => {
    if (!currentUser) return false;
    
    const orgRole = currentUser.orgRole;
    
    switch (permission) {
      case 'view_all':
        return orgRole === 'org_admin';
      case 'manage_budgets':
        return orgRole === 'org_admin';
      case 'approve_requests':
        return orgRole === 'org_admin';
      case 'view_dept':
        return ['org_admin', 'dept_manager', 'team_lead'].includes(orgRole);
      case 'manage_dept':
        return ['org_admin', 'dept_manager'].includes(orgRole);
      case 'create_expense':
        return ['org_admin', 'dept_manager'].includes(orgRole);
      case 'create_request':
        return ['org_admin', 'dept_manager'].includes(orgRole);
      case 'view_analytics':
        return ['org_admin', 'dept_manager'].includes(orgRole);
      default:
        return false;
    }
  }, [currentUser]);

  const isOrgAdmin = currentUser?.orgRole === 'org_admin';
  const isDeptManager = currentUser?.orgRole === 'dept_manager' || currentUser?.departmentRole === 'dept_manager';
  const userDepartmentId = currentUser?.departmentId;

  // ============================================
  // SUBSCRIPTION LIMIT CHECK
  // ============================================
  
  const checkSubscriptionLimit = useCallback((limitType) => {
    if (!subscription?.limits || !subscription?.usage) return { allowed: true };
    
    const limit = subscription.limits[limitType];
    const current = subscription.usage[limitType.replace('max', 'current')];
    
    if (limit === -1) return { allowed: true, unlimited: true };
    
    return {
      allowed: current < limit,
      current,
      limit,
      remaining: limit - current
    };
  }, [subscription]);

  // ============================================
  // EFFECTS
  // ============================================

  // Fetch subscription on mount and when organization changes
  useEffect(() => {
    if (organization?._id) {
      fetchSubscription();
    }
  }, [organization?._id, fetchSubscription]);

  // Fetch finance data when subscription is active
  // Using hasActiveSubscription as the only dependency to prevent infinite loops
  useEffect(() => {
    if (hasActiveSubscription) {
      // Helper to handle permission errors silently (403 = expected for non-finance users)
      const handleApiError = (apiName) => (err) => {
        const status = err?.response?.status;
        // 403 errors are expected for non-finance users - handle silently
        if (status === 403) {
          // Silent - user doesn't have permission, this is expected behavior
          return null;
        }
        // Log other errors as warnings (not errors) to avoid red console messages
        console.warn(`${apiName}: ${err?.message || 'Request failed'}`);
        return null;
      };

      // Fetch all data once when subscription becomes active
      Promise.all([
        orgFinanceService.getFinanceConfig().catch(handleApiError('getFinanceConfig')),
        orgFinanceService.getDashboard().catch(handleApiError('getDashboard')),
        orgFinanceService.getOverview().catch(handleApiError('getOverview')),
        orgFinanceService.getAnalytics().catch(handleApiError('getAnalytics')),
        orgFinanceService.getAllDepartmentBudgets().catch(handleApiError('getAllDepartmentBudgets')),
        orgFinanceService.getBudgetRequests().catch(handleApiError('getBudgetRequests')),
        orgFinanceService.getExpenses().catch(handleApiError('getExpenses'))
      ]).then(([configRes, dashRes, overviewRes, analyticsRes, budgetsRes, requestsRes, expensesRes]) => {
        let userIsFinance = false;
        
        if (configRes?.success) {
          setFinanceConfig(configRes.data);
          userIsFinance = configRes.isFinanceUser || false;
          setIsFinanceUser(userIsFinance);
        }
        if (dashRes?.success) {
          setDashboardData(dashRes.data);
          setDashboardViewType(dashRes.viewType || 'department_portal');
          userIsFinance = dashRes.isFinanceUser || userIsFinance;
          setIsFinanceUser(userIsFinance);
          
          // For non-finance users, use departmentBudgets from dashboard response
          // since getAllDepartmentBudgets is restricted to finance users
          if (!userIsFinance && dashRes.data?.departmentBudgets) {
            setDepartmentBudgets(dashRes.data.departmentBudgets);
          }
          
          // Use expenses from dashboard if getExpenses failed
          // Dashboard returns recentExpenses for finance users, myExpenses for non-finance
          if (!expensesRes?.success) {
            const dashExpenses = dashRes.data?.recentExpenses || dashRes.data?.myExpenses || [];
            setExpenses(dashExpenses);
          }
        }
        if (overviewRes?.success) {
          setOverview(overviewRes.data);
          setTrialInfo(overviewRes.trialInfo);
        }
        if (analyticsRes?.success) {
          setAnalytics(analyticsRes.data);
        }
        // Only set budgets from getAllDepartmentBudgets if finance user (and it succeeded)
        if (budgetsRes?.success && budgetsRes.data?.length > 0) {
          setDepartmentBudgets(budgetsRes.data || []);
        }
        if (requestsRes?.success) {
          setBudgetRequests(requestsRes.data || []);
          const pending = (requestsRes.data || []).filter(
            r => r.status === 'pending' || r.status === 'submitted'
          ).length;
          setPendingRequestsCount(pending);
        }
        // Set expenses from API response
        if (expensesRes?.success) {
          setExpenses(expensesRes.data || []);
        }
      });
    }
  }, [hasActiveSubscription]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = {
    // Subscription
    subscription,
    subscriptionLoading,
    isTrialActive,
    trialDaysRemaining,
    canActivate,
    hasActiveSubscription,
    isSubscriptionExpired,
    activateTrial,
    upgradeSubscription,
    fetchSubscription,
    checkSubscriptionLimit,
    
    // Overview
    overview,
    overviewLoading,
    fetchOverview,
    updateOverview,
    trialInfo,
    
    // Analytics
    analytics,
    analyticsLoading,
    fetchAnalytics,
    
    // Department Budgets
    departmentBudgets,
    fetchDepartmentBudgets,
    
    // Finance Department Configuration
    financeConfig,
    financeConfigLoading,
    isFinanceUser,
    fetchFinanceConfig,
    configureFinanceDepartment,
    
    // Dashboard (Role-Based)
    dashboardData,
    dashboardLoading,
    dashboardViewType,
    fetchDashboard,
    
    // Expenses
    expenses,
    expensesLoading,
    fetchExpenses,
    
    // Budget Requests
    budgetRequests,
    pendingRequestsCount,
    fetchBudgetRequests,
    createBudgetRequest,
    approveBudgetRequest,
    rejectBudgetRequest,
    requestClarification,
    addRequestComment,
    
    // Refresh
    refreshData,
    
    // Setup state
    needsSetup,
    
    // Permissions
    hasFinancePermission,
    isOrgAdmin,
    isDeptManager,
    userDepartmentId,
    
    // Error
    error
  };

  return (
    <OrgFinanceContext.Provider value={value}>
      {children}
    </OrgFinanceContext.Provider>
  );
};

export default OrgFinanceContext;
